/**
 * Content Stage — Stage 3: Content quality validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.4
 *
 * Validates:
 * - Explanation quality (not generic, has didactic depth)
 * - Distractor explanations (present, not generic, reference misconceptions)
 * - Forbidden vocabulary per tier
 * - Question text length per tier
 * - Module-specific rules (from governance validator)
 * - Notation requirements (LaTeX, comma decimals, units)
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { blocker, coach, info } from '../issueContract'
import {
  getPolicy,
  getTierBand,
  getForbiddenVocab,
  getMaxQuestionLength,
  getMinExplanationLength,
  getMinDistractorLength,
  getNotationRequirements,
  getDistractorPattern,
  requiresRuleReference,
  getCheckStrategyMinTier,
  isFeatureEnabled,
} from '../policyEngine'
import {
  isGenericText,
  isDidactic,
  GENERIC_EXPLANATION_PATTERNS,
  GENERIC_DISTRACTOR_PATTERNS,
  detectJargon,
  hasPassiveVoice,
  detectLongSentences,
  getMaxWordsPerSentence,
} from '../../utils'
import type { Question, ModuleId } from '../../../types'

const STAGE: PipelineStage = 3

/**
 * Run content quality validation on a question.
 */
export function runContentStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const tier = question.tier ?? 1
  const module = question.module

  // ── Forbidden vocabulary ─────────────────────────────────────────────────

  const forbidden = getForbiddenVocab(tier)
  const questionText = (question.question ?? '').toLowerCase()
  for (const word of forbidden) {
    if (questionText.includes(word.toLowerCase())) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_FORBIDDEN_VOCAB',
          `Word "${word}" too complex for tier ${tier} (${getTierBand(tier)} band)`,
          {
            field: 'question',
            actual: word,
            questionId: qId,
            familyId: fId,
            severity: 40,
            suggestion: `Use simpler vocabulary appropriate for tier ${tier}`,
          },
        ),
      )
    }
  }

  // ── Question text length ─────────────────────────────────────────────────

  const maxLength = getMaxQuestionLength(tier, module)
  if (questionText.length > maxLength) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_QUESTION_TOO_LONG',
        `Question text (${questionText.length} chars) exceeds max ${maxLength} for tier ${tier}`,
        {
          field: 'question',
          expected: `≤${maxLength}`,
          actual: String(questionText.length),
          questionId: qId,
          familyId: fId,
          severity: 35,
        },
      ),
    )
  }

  // ── Explanation quality ──────────────────────────────────────────────────

  const explanation = question.feedback?.explanationText?.trim() ?? ''
  if (explanation) {
    // Min length check
    const minExplanation = getMinExplanationLength(tier)
    if (explanation.length < minExplanation) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_EXPLANATION_TOO_SHORT',
          `Explanation (${explanation.length} chars) below minimum ${minExplanation} for tier ${tier}`,
          {
            field: 'feedback.explanationText',
            expected: `≥${minExplanation}`,
            actual: String(explanation.length),
            questionId: qId,
            familyId: fId,
            severity: 50,
          },
        ),
      )
    }

    // Generic check
    if (isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS)) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_GENERIC_EXPLANATION',
          'Explanation is generic/boilerplate — must explain the concept or calculation',
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 55,
            suggestion:
              'Add step-by-step reasoning, reference the rule, or show the calculation',
          },
        ),
      )
    }

    // Didactic depth check (only if not generic)
    if (
      !isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS) &&
      explanation.length >= 30
    ) {
      if (!isDidactic(explanation)) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_EXPLANATION_NOT_DIDACTIC',
            'Explanation lacks didactic depth — should show steps, reference rules, or explain reasoning',
            {
              field: 'feedback.explanationText',
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }

    // ── NEW: Jargon detection (CONTENT_GOVERNANCE_FRAMEWORK §1.1) ────────
    const jargonFound = detectJargon(explanation)
    for (const { term, replacement } of jargonFound) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_JARGON_DETECTED',
          `Jargon "${term}" in explanation — too complex for children aged 9-11`,
          {
            field: 'feedback.explanationText',
            actual: term,
            questionId: qId,
            familyId: fId,
            severity: 35,
            suggestion: `Replace "${term}" with ${replacement}`,
          },
        ),
      )
    }

    // ── NEW: Passive voice detection (CONTENT_GOVERNANCE_FRAMEWORK §4.1) ─
    if (hasPassiveVoice(explanation)) {
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_EXPLANATION_PASSIVE',
          'Explanation uses passive voice — use active voice ("kijk", "tel", "schrijf")',
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 15,
            suggestion: 'Replace passive constructions with active verbs',
          },
        ),
      )
    }

    // ── NEW: Sentence length check (CONTENT_GOVERNANCE_FRAMEWORK §1.2, §4.1) ─
    const longSentences = detectLongSentences(explanation, tier)
    if (longSentences.length > 0) {
      const maxWords = getMaxWordsPerSentence(tier)
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_SENTENCE_TOO_LONG',
          `${longSentences.length} sentence(s) exceed the ${maxWords}-word limit for tier ${tier}`,
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 20,
            suggestion: `Keep sentences ≤${maxWords} words for tier ${tier} (groep ${tier <= 4 ? 6 : tier <= 8 ? 7 : 8})`,
          },
        ),
      )
    }

    // Module-specific rule reference
    if (module && requiresRuleReference(module)) {
      // Check if explanation references a rule (module-specific patterns)
      const pattern = getDistractorPattern(module)
      if (pattern && !pattern.test(explanation) && !isDidactic(explanation)) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_MISSING_RULE_REF',
            `Explanation should reference module-specific rules for ${module}`,
            {
              field: 'feedback.explanationText',
              questionId: qId,
              familyId: fId,
              severity: 20,
            },
          ),
        )
      }
    }

    // Check strategy requirement (SWW tier 7+)
    if (module) {
      const checkMinTier = getCheckStrategyMinTier(module)
      if (checkMinTier !== null && tier >= checkMinTier) {
        const CHECK_STRATEGY =
          /ik-vorm|hij-vorm|langer maken|worden|ik loop|hij loopt|zet.*om/i
        if (!CHECK_STRATEGY.test(explanation)) {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_MISSING_CHECK_STRATEGY',
              `Explanation for ${module} tier ≥${checkMinTier} should include a check strategy`,
              {
                field: 'feedback.explanationText',
                questionId: qId,
                familyId: fId,
                severity: 40,
              },
            ),
          )
        }
      }
    }
  }

  // ── Distractor explanations ──────────────────────────────────────────────

  if (question.questionType === 'multiple_choice' && question.options) {
    const feedbackMap = question.feedback?.distractorExplanations ?? {}
    const optionCount = question.options.length

    // Check all options have explanations
    const policy = getPolicy()
    if (policy.featureFlags.enforceDistractorExplanations) {
      for (let i = 0; i < optionCount; i++) {
        const exp = feedbackMap[String(i)]
        if (!exp || typeof exp !== 'string' || exp.trim() === '') {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_MISSING_DISTRACTOR_EXPLANATION',
              `Missing distractor explanation for option ${i}`,
              {
                field: `feedback.distractorExplanations.${i}`,
                questionId: qId,
                familyId: fId,
                severity: 45,
              },
            ),
          )
        }
      }
    }

    // Check distractor quality
    const minDistractor = getMinDistractorLength(tier)
    for (const [idx, exp] of Object.entries(feedbackMap)) {
      if (typeof exp !== 'string' || exp.trim() === '') continue

      // Length check
      if (exp.trim().length < minDistractor) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_DISTRACTOR_TOO_SHORT',
            `Distractor explanation for option ${idx} (${exp.trim().length} chars) below minimum ${minDistractor}`,
            {
              field: `feedback.distractorExplanations.${idx}`,
              questionId: qId,
              familyId: fId,
              severity: 15,
            },
          ),
        )
      }

      // Generic check
      if (isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS)) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_GENERIC_DISTRACTOR',
            `Distractor explanation for option ${idx} is generic — must name the specific misconception`,
            {
              field: `feedback.distractorExplanations.${idx}`,
              questionId: qId,
              familyId: fId,
              severity: 45,
              suggestion:
                'Explain WHY this answer is wrong and what misconception it represents',
            },
          ),
        )
      }

      // ── NEW: Jargon check on distractor text (CONTENT_GOVERNANCE_FRAMEWORK §1.1) ─
      const distractorJargon = detectJargon(exp)
      for (const { term, replacement } of distractorJargon) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_JARGON_DETECTED',
            `Jargon "${term}" in distractor explanation for option ${idx}`,
            {
              field: `feedback.distractorExplanations.${idx}`,
              actual: term,
              questionId: qId,
              familyId: fId,
              severity: 30,
              suggestion: `Replace "${term}" with ${replacement}`,
            },
          ),
        )
      }
    }

    // Misconception reference check
    if (module) {
      const misconceptionPattern = getDistractorPattern(module)
      if (misconceptionPattern) {
        const wrongEntries = Object.entries(feedbackMap).filter(
          ([idx]) => Number(idx) !== question.correctAnswer,
        )
        if (wrongEntries.length > 0) {
          const hasMisconceptionRef = wrongEntries.some(
            ([, exp]) =>
              typeof exp === 'string' &&
              (misconceptionPattern.test(exp) || isDidactic(exp)),
          )
          if (!hasMisconceptionRef) {
            issues.push(
              coach(
                'content',
                STAGE,
                'CONTENT_NO_MISCONCEPTION_REF',
                `No distractor explanation references a ${module}-specific misconception`,
                {
                  field: 'feedback.distractorExplanations',
                  questionId: qId,
                  familyId: fId,
                  severity: 40,
                },
              ),
            )
          }
        }
      }
    }
  }

  // ── Notation enforcement ─────────────────────────────────────────────────

  if (module) {
    const notation = getNotationRequirements(module)
    const fullText =
      (question.question ?? '') + ' ' + (question.options?.join(' ') ?? '')

    // LaTeX fractions
    if (notation.requireLatexFractions && isFeatureEnabled('enforceLatex')) {
      const isFractionContext =
        fId.includes('BREUK') ||
        (question.skill ?? '').toLowerCase().includes('breuk')
      if (
        isFractionContext &&
        fullText.includes('/') &&
        !fullText.includes('\\frac')
      ) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_MISSING_LATEX',
            'Fractions should use LaTeX (\\frac{}{}) instead of /',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 35,
            },
          ),
        )
      }
    }

    // Comma decimals
    if (notation.requireCommaDecimals) {
      if (fullText.match(/\d+\.\d+/) && !fId.includes('WETENSCHAPPELIJK')) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_DOT_DECIMAL',
            'Use comma (,) for decimals, not dot (.)',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }

    // Euro comma
    if (notation.requireEuroComma) {
      if (fullText.includes('€') && fullText.match(/\d+\.\d{2}/)) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_EURO_DOT',
            'Use comma (€3,50) not dot (€3.50) for euro notation',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }

    // Superscript units
    if (
      notation.requireSuperscriptUnits &&
      isFeatureEnabled('enforceUnitNotation')
    ) {
      const requiresSuperscript = fId.includes('OPP') || fId.includes('INH')
      if (
        requiresSuperscript &&
        (fullText.includes('m2') ||
          fullText.includes('cm2') ||
          fullText.includes('m3'))
      ) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_MISSING_SUPERSCRIPT',
            'Use superscripts (m², cm³) instead of m2, cm3',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 35,
            },
          ),
        )
      }
    }
  }

  return issues
}
